// Metas de SLA (spec §6.7), em minutos corridos, e utilidades de exibição.
export type SlaStage = "company" | "contract" | "signature" | "payment";
export type SlaColor = "green" | "amber" | "red";
export type PackageSla = {
  stage: SlaStage;
  clockStartedAt: string;
  elapsedMin: number;
  targetMin: number;
  challengeMin: number;
  color: SlaColor;
  breachOpen: boolean;
};

export const SLA_TARGETS: Record<SlaStage, { targetMin: number; challengeMin: number }> = {
  company: { targetMin: 960, challengeMin: 480 },
  contract: { targetMin: 480, challengeMin: 240 },
  signature: { targetMin: 720, challengeMin: 360 },
  payment: { targetMin: 720, challengeMin: 360 },
};

export const STAGE_LABEL: Record<SlaStage, string> = {
  company: "Empresa",
  contract: "Antecipe · contrato",
  signature: "Assinatura",
  payment: "Pagamento",
};

export const STAGE_BY_STATUS: Record<string, SlaStage> = {
  waiting_supplier_company_input_vacancies_data: "company",
  waiting_generate_doctor_contract: "contract",
  waiting_doctor_sign_contract: "signature",
  waiting_payment_doctor: "payment",
};

export const isSlaStage = (value: unknown): value is SlaStage => typeof value === "string" && value in SLA_TARGETS;

/** Cores G1: verde ≤ challenge, âmbar ≤ meta, vermelho > meta. */
export const slaColor = (elapsed: number, challenge: number, target: number): SlaColor =>
  elapsed <= challenge ? "green" : elapsed <= target ? "amber" : "red";

const HOUR_MS = 3_600_000;
const BRT_OFFSET_MS = 3 * HOUR_MS; // BRT fixo (sem horário de verão desde 2019)
// Horário sem fuso (DATETIME do BE) é tratado como BRT.
const toBrtMs = (iso: string) => (/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? Date.parse(iso) - BRT_OFFSET_MS : Date.parse(`${iso.replace(" ", "T")}Z`));

/** Corte útil, só para exibição: seg–sex 08–18h BRT, sem feriados. */
export function businessMinutesBetween(fromIso: string, to: Date): number {
  let cur = toBrtMs(fromIso);
  const end = to.getTime() - BRT_OFFSET_MS;
  if (!Number.isFinite(cur)) return 0;
  let total = 0;
  while (cur < end) {
    const d = new Date(cur);
    const dayStart = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const dow = d.getUTCDay();
    if (dow >= 1 && dow <= 5) {
      const a = Math.max(cur, dayStart + 8 * HOUR_MS);
      const b = Math.min(end, dayStart + 18 * HOUR_MS);
      if (b > a) total += b - a;
    }
    cur = dayStart + 24 * HOUR_MS;
  }
  return Math.round(total / 60_000);
}

/** "13 h 20 min", "45 min", "8 h". */
export const formatMinutes = (min: number) => {
  const safe = Math.max(0, Math.round(min));
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
};
